/**
 * Chaport integration с поддержкой logged-in users API.
 *
 * Эталонная реализация — см. `.meta/playbooks/chaport-logged-in-users.md` в зонтичном
 * репо `_crypto-services`. Все ключевые тонкости (порядок инициализации, отложенный
 * инжект `insert.js`, polling вместо `on('ready')`, гейт identity для метрик) описаны
 * там и в комментариях ниже.
 *
 * Docs: https://docs.chaport.ru/api/js/v1/#logged-in-users
 */

interface ChaportVisitorData {
  email?: string;
  name?: string;
  phone?: string;
  custom?: Record<string, string | number>;
}

interface ChaportSessionCredentials {
  id: string;
  token: string;
}

interface ChaportStartSessionInput extends ChaportSessionCredentials {
  visitorData?: ChaportVisitorData;
}

interface ChaportApi {
  hideLauncher: () => void;
  newPageView: () => void;
  open: () => void;
  setVisitorData: (data: ChaportVisitorData) => void;
  startSession: (credentials: ChaportSessionCredentials) => void;
  stopSession: (opts?: { clearCache?: boolean }) => void;
}

interface ChaportConfig {
  appId: string;
}

interface ChaportWindow extends Window {
  chaport?: Partial<ChaportApi> & {
    _l?: Record<string, Array<() => void>>;
    _q?: unknown[];
    on?: (event: string, callback: () => void) => void;
    q?: (method: string, args?: unknown[]) => void;
  };
  chaportConfig?: ChaportConfig;
}

interface ChaportBootstrap extends Partial<ChaportApi> {
  _l: Record<string, Array<() => void>>;
  _q: unknown[];
  on: (event: string, callback: () => void) => void;
  q: (method: string, args?: unknown[]) => void;
}

const CHAPORT_APP_ID = '69c3c490ea9d6e7426496307'
const CHAPORT_SCRIPT_ID = 'chaport-script'
const CHAPORT_SCRIPT_SRC = 'https://app.chaport.ru/javascripts/insert.js'

const getWindow = (): ChaportWindow => window as ChaportWindow

const queueChaportCall = (method: string, args?: unknown[]): void => {
  const chaport = getWindow().chaport
  const chaportMethod = chaport?.[method as keyof ChaportApi] as
    | ((...methodArgs: unknown[]) => void)
    | undefined

  if (typeof chaportMethod === 'function') {
    chaportMethod(...(args ?? []))
    return
  }

  if (typeof chaport?.q === 'function') {
    chaport.q(method, args)
  }
}

const ensureChaportBootstrap = (): void => {
  const chaportWindow = getWindow()

  if (chaportWindow.chaport !== undefined) {
    return
  }

  chaportWindow.chaportConfig = {
    appId: CHAPORT_APP_ID
  }

  const chaport: ChaportBootstrap = {
    _l: {},
    _q: [],
    q (method: string, args?: unknown[]) {
      this._q.push(args === undefined ? [method] : [method, ...args])
    },
    on (event: string, callback: () => void) {
      const listeners = this._l[event]
      if (listeners === undefined) {
        this._l = {
          ...this._l,
          [event]: [callback]
        }
        return
      }

      listeners.push(callback)
    }
  }

  chaportWindow.chaport = chaport
}

let chaportScriptInjected = false

const injectChaportScript = (): void => {
  if (chaportScriptInjected || document.getElementById(CHAPORT_SCRIPT_ID) !== null) {
    return
  }

  chaportScriptInjected = true

  const script = document.createElement('script')
  script.id = CHAPORT_SCRIPT_ID
  script.type = 'text/javascript'
  script.async = true
  script.src = CHAPORT_SCRIPT_SRC

  const firstScript = document.getElementsByTagName('script')[0]

  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
    return
  }

  document.head.appendChild(script)
}

const CHAPORT_IDENTITY_TIMEOUT_MS = 5000

/**
 * Готовит окружение Chaport, но НЕ загружает sdk-скрипт. Скрипт грузится только
 * когда определена identity (startChaportSession или markChaportAnonymous), иначе
 * Chaport SDK успевает создать анонимного visitor'а до того, как наш startSession
 * доходит — и iframe фиксируется на UUID, история не агрегируется по нашему id.
 *
 * В passport-frontend нативный launcher Chaport (плавающая кнопка чата) — это
 * основная точка входа в чат: отдельной кнопки в интерфейсе нет. Поэтому
 * hideLauncher здесь НЕ зовём. Если в другом проекте кнопка чата своя — там
 * имеет смысл подписаться на `ready` и спрятать launcher.
 */
export const initChaport = (): void => {
  ensureChaportBootstrap()

  // Safety net: если за CHAPORT_IDENTITY_TIMEOUT_MS никто не вызвал ни
  // startChaportSession, ни markChaportAnonymous (например, страница без auth-флоу
  // или /user/ затупил), сбрасываем гейт и пускаем буферизованные вызовы как
  // анонима — иначе виджет вообще никогда не оживёт.
  setTimeout(() => {
    if (!isIdentityResolved) {
      markChaportAnonymous()
    }
  }, CHAPORT_IDENTITY_TIMEOUT_MS)
}

let currentVisitorId: string | null = null

/**
 * Гейт «личность определена».
 *
 * Все обращения к Chaport, которые могут спровоцировать создание visitor'а
 * (setVisitorData / newPageView), буферизуются до того, как мы либо вызвали
 * startChaportSession (logged-in user), либо явно сказали markChaportAnonymous
 * (юзер не авторизован, работаем анонимно).
 */
let isIdentityResolved = false
const pendingCalls: Array<{ method: string; args?: unknown[] }> = []

const callOrBuffer = (method: string, args?: unknown[]): void => {
  if (!isIdentityResolved) {
    pendingCalls.push({ method, args })
    return
  }

  queueChaportCall(method, args)
}

const flushPendingCalls = (): void => {
  while (pendingCalls.length > 0) {
    const call = pendingCalls.shift()

    if (call === undefined) {
      continue
    }

    queueChaportCall(call.method, call.args)
  }
}

export const startChaportSession = (input: ChaportStartSessionInput): void => {
  if (currentVisitorId === input.id) {
    if (input.visitorData !== undefined) {
      queueChaportCall('setVisitorData', [input.visitorData])
    }
    return
  }

  if (currentVisitorId !== null) {
    queueChaportCall('stopSession', [{ clearCache: true }])
  }

  const credentials: ChaportSessionCredentials = { id: input.id, token: input.token }

  callChaportStartSessionOnce(credentials)

  if (input.visitorData !== undefined) {
    queueChaportCall('setVisitorData', [input.visitorData])
  }

  currentVisitorId = input.id
  isIdentityResolved = true
  flushPendingCalls()
  injectChaportScript()
}

let startSessionTimer: number | null = null

/**
 * Вызывает Chaport startSession ровно один раз, как только SDK реально готов.
 *
 * Не используем `on('ready', ...)` намеренно: каждый on() пушит callback в _l
 * без возможности отписаться. Если startChaportSession будет вызван повторно
 * (HMR / router-guard / повторный логин), накопится несколько ready-подписчиков
 * → несколько startSession → лавина /init-запросов и 503-rate-limit от Chaport.
 *
 * `startSession` из очереди _q SDK дренит, но идентичность не переключает,
 * поэтому нужен direct-call.
 */
const callChaportStartSessionOnce = (credentials: ChaportSessionCredentials): void => {
  if (startSessionTimer !== null) {
    window.clearInterval(startSessionTimer)
    startSessionTimer = null
  }

  const tryCall = (): boolean => {
    const method = getWindow().chaport?.startSession

    if (typeof method !== 'function') {
      return false
    }

    method(credentials)
    return true
  }

  if (tryCall()) {
    return
  }

  startSessionTimer = window.setInterval(() => {
    if (tryCall() && startSessionTimer !== null) {
      window.clearInterval(startSessionTimer)
      startSessionTimer = null
    }
  }, 100)
}

/**
 * Сигнал, что юзер не авторизован — продолжаем как аноним. Размораживает буфер
 * вызовов и инжектит sdk-скрипт. Должна быть вызвана, если /user/ не вернул
 * visitor_hash (например, гость или открытые страницы).
 */
export const markChaportAnonymous = (): void => {
  if (isIdentityResolved) {
    return
  }

  isIdentityResolved = true
  flushPendingCalls()
  injectChaportScript()
}

export const stopChaportSession = (): void => {
  if (currentVisitorId === null) {
    return
  }

  queueChaportCall('stopSession', [{ clearCache: true }])
  currentVisitorId = null
}

export const openChaport = (data?: ChaportVisitorData): void => {
  if (data !== undefined) {
    queueChaportCall('setVisitorData', [data])
  }

  queueChaportCall('open')
}

export const sendChaportPageView = (): void => {
  callOrBuffer('newPageView')
}

export const updateChaportNotes = (notes: string): void => {
  callOrBuffer('setVisitorData', [
    {
      custom: {
        notes
      }
    }
  ])
}
